import { createAction, props } from '@ngrx/store';

import { BeneficiaryDependentUpdateDto } from '../../../modules/beneficiaries/dtos/beneficiary-dependent-update';
import { BeneficiaryStatusFilter } from '../../../modules/beneficiaries/enums/beneficiary-status-filter.enum';
import { OrganizationDto } from '../../../modules/organizations/dtos/organization';
import {
	OperationType,
	UploadStatusFilter,
} from '../../../shared/enums/batch-actions.enum';
import { TemplateFileType } from '../../../shared/enums/template-file.enum';
import {
	BeneficiaryBenefitData,
	BeneficiaryInfoResponse,
	EditBeneStatusRequest,
	OrgBeneficiary,
	OrgBeneficiaryPendencies,
	OrgBeneficiaryPendenciesRequest,
	TitularRegistration,
	UserDataModel,
} from '../models/beneficiaries.model';
import {
	BenefitRequest,
	BenefitResponse,
	BenefitSummary,
} from '../models/benefits.model';
import { INotification } from '../models/notification';
import {
	AddressResult,
	DashBeneInfo,
	DashChartInfo,
	DashReportChart,
	GroupsListSummary,
	HeadquarterSummary,
	NewOrganizationRequest,
	OrgGroup,
	OrgHubItem,
	OrgListSummary,
	OrgRegistration,
	OrgUser,
	PartnerRegistration,
	PartnerRequest,
	UserEditRequest,
	UserRegistration,
	UserRequest,
} from '../models/organizations.model';
import {
	PolicyRegistrationResponse,
	PolicySummary,
} from '../models/policies.model';
import { PositionRequest, PositionSummary } from '../models/positions.model';
import { ProductsSummary } from '../models/products.model';
import { Score } from '../models/score.model';

export const restoreFormStart = createAction(
	'[organizations] restore form start',
);
export const restoreFormReset = createAction(
	'[organizations] restore form reset',
);

export const fetchOrgListSuccess = createAction(
	'[organizations] fetch org list success',
	props<{ organizations: OrgGroup[] }>(),
);

export const toggleFavOrgStart = createAction(
	'[organizations] toggle favorite org start',
	props<{ id: string; current: boolean }>(),
);
export const toggleFavOrgFinish = createAction(
	'[organizations] toggle favorite org finish',
);

export const fetchOrgHubStart = createAction(
	'[organizations] fetch org hub start',
	props<{ id: string }>(),
);
export const fetchOrgHubSuccess = createAction(
	'[organizations] fetch org hub success',
	props<{ orghub: OrgHubItem[] }>(),
);
export const fetchOrgHubFailure = createAction(
	'[organizations] fetch org hub failure',
);
export const fetchOrgHubClear = createAction(
	'[organizations] fetch org hub clear',
);

export const fetchHeadquartersStart = createAction(
	'[organizations] fetch headquarter orgs list start',
);
export const fetchHeadquartersSuccess = createAction(
	'[organizations] fetch headquarter orgs list success',
	props<{ headquarters: HeadquarterSummary[] }>(),
);
export const fetchHeadquartersFailure = createAction(
	'[organizations] fetch headquarter orgs list failure',
);

export const fetchCnpjAvailabilityStart = createAction(
	'[organizations] fetch cnpj availability start',
	props<{ category: string; cnpj: string }>(),
);
export const fetchCnpjAvailabilitySuccess = createAction(
	'[organizations] fetch cnpj availability success',
	props<{ result: boolean }>(),
);
export const fetchCnpjAvailabilityFailure = createAction(
	'[organizations] fetch cnpj availability failure',
);
export const fetchCnpjAvailabilityClear = createAction(
	'[organizations] fetch cnpj availability clear',
);

export const fetchAddressByCEPStart = createAction(
	'[organizations] fetch address by cep start',
	props<{ cep: string }>(),
);
export const fetchAddressByCEPSuccess = createAction(
	'[organizations] fetch address by cep success',
	props<{ address: AddressResult }>(),
);
export const fetchAddressByCEPFailure = createAction(
	'[organizations] fetch address by cep failure',
);
export const fetchAddressByCEPClear = createAction(
	'[organizations] fetch address by cep clear',
);

export const fetchUFCitiesStart = createAction(
	'[organizations] fetch uf cities list start',
	props<{ id: number }>(),
);
export const fetchUFCitiesSuccess = createAction(
	'[organizations] fetch uf cities list success',
	props<{ cities: string[] }>(),
);
export const fetchUFCitiesFailure = createAction(
	'[organizations] fetch uf cities list failure',
);
export const fetchUFCitiesClear = createAction(
	'[organizations] fetch uf cities list clear',
);

export const fetchAuxUFCitiesStart = createAction(
	'[organizations] fetch aux uf cities list start',
	props<{ id: number }>(),
);
export const fetchAuxUFCitiesSuccess = createAction(
	'[organizations] fetch aux uf cities list success',
	props<{ cities: string[] }>(),
);
export const fetchAuxUFCitiesFailure = createAction(
	'[organizations] fetch aux uf cities list failure',
);
export const fetchAuxUFCitiesClear = createAction(
	'[organizations] fetch aux uf cities list clear',
);

export const createOrgStart = createAction(
	'[organizations] create organization start',
	props<{ request: NewOrganizationRequest }>(),
);
export const createOrgSuccess = createAction(
	'[organizations] create organization success',
);
export const createOrgFailure = createAction(
	'[organizations] create organization failure',
);

export const fetchOrgRegStart = createAction(
	'[organizations] fetch org registration start',
	props<{ id: string }>(),
);
export const fetchOrgRegSuccess = createAction(
	'[organizations] fetch org registration success',
	props<{ registration: OrgRegistration }>(),
);
export const fetchOrgRegFailure = createAction(
	'[organizations] fetch org registration failure',
);

export const editOrgRegStart = createAction(
	'[organizations] edit org registration start',
	props<{ id: string; request: OrgRegistration }>(),
);
export const editOrgRegSuccess = createAction(
	'[organizations] edit org registration success',
);
export const editOrgRegFailure = createAction(
	'[organizations] edit org registration failure',
);

export const fetchPartnersStart = createAction(
	'[organizations] fetch partners start',
);
export const fetchPartnersSuccess = createAction(
	'[organizations] fetch partners success',
	props<{ partners: PartnerRegistration[] }>(),
);
export const fetchPartnersFailure = createAction(
	'[organizations] fetch partners failure',
);
export const fetchPartnersClear = createAction(
	'[organizations] fetch partners clear',
);

export const createPartnerStart = createAction(
	'[organizations] create partner start',
	props<{ request: PartnerRequest }>(),
);
export const createPartnerSuccess = createAction(
	'[organizations] create partner success',
);
export const createPartnerFailure = createAction(
	'[organizations] create partner failure',
);

export const fetchPartnerRegStart = createAction(
	'[organizations] fetch partner registration start',
	props<{ id: string }>(),
);
export const fetchPartnerRegSuccess = createAction(
	'[organizations] fetch partner registration success',
	props<{ partner: PartnerRegistration }>(),
);
export const fetchPartnerRegFailure = createAction(
	'[organizations] fetch partner registration failure',
);

export const editPartnerRegStart = createAction(
	'[organizations] edit partner registration start',
	props<{ id: string; request: PartnerRequest }>(),
);
export const editPartnerRegSuccess = createAction(
	'[organizations] edit partner registration success',
);
export const editPartnerRegFailure = createAction(
	'[organizations] edit partner registration failure',
);

export const fetchOrgBenefitsStart = createAction(
	'[organizations] fetch org benefits start',
	props<{ id: string }>(),
);
export const fetchOrgBenefitsSuccess = createAction(
	'[organizations] fetch org benefits success',
	props<{ benefits: BenefitSummary[] }>(),
);
export const fetchOrgBenefitsFailure = createAction(
	'[organizations] fetch org benefits failure',
);
export const fetchOrgBenefitsClear = createAction(
	'[organizations] fetch org benefits clear',
);

export const fetchBenefitRegStart = createAction(
	'[organizations] fetch benefit registration start',
	props<{ id: string; benefitId: string }>(),
);
export const fetchBenefitRegSuccess = createAction(
	'[organizations] fetch benefit registration success',
	props<{ benefit: BenefitResponse }>(),
);
export const fetchBenefitRegFailure = createAction(
	'[organizations] fetch benefit registration failure',
);

export const createBenefitStart = createAction(
	'[organizations] create benefit start',
	props<{ id: string; request: BenefitRequest }>(),
);
export const createBenefitSuccess = createAction(
	'[organizations] create benefit success',
);
export const createBenefitFailure = createAction(
	'[organizations] create benefit failure',
);

export const editBenefitRegStart = createAction(
	'[organizations] edit benefit registration start',
	props<{ id: string; benefitId: string; request: BenefitRequest }>(),
);
export const editBenefitRegSuccess = createAction(
	'[organizations] edit benefit registration success',
);
export const editBenefitRegFailure = createAction(
	'[organizations] edit benefit registration failure',
);

export const fetchOrgPositionsStart = createAction(
	'[organizations] fetch org positions start',
	props<{ id: string }>(),
);
export const fetchOrgPositionsSuccess = createAction(
	'[organizations] fetch org positions success',
	props<{ positions: PositionSummary[] }>(),
);
export const fetchOrgPositionsFailure = createAction(
	'[organizations] fetch org positions failure',
);
export const fetchOrgPositionsClear = createAction(
	'[organizations] fetch org positions clear',
);

export const fetchPositionRegStart = createAction(
	'[organizations] fetch position registration start',
	props<{ id: string; positionId: string }>(),
);
export const fetchPositionRegSuccess = createAction(
	'[organizations] fetch position registration success',
	props<{ position: unknown }>(),
);
export const fetchPositionRegFailure = createAction(
	'[organizations] fetch position registration failure',
);

export const createPositionStart = createAction(
	'[organizations] create position start',
	props<{ id: string; request: PositionRequest }>(),
);
export const createPositionSuccess = createAction(
	'[organizations] create position success',
);
export const createPositionFailure = createAction(
	'[organizations] create position failure',
);

export const cloneOrgPositionStart = createAction(
	'[organizations] clone org position start',
	props<{ id: string; request: { id: string } }>(),
);
export const cloneOrgPositionSuccess = createAction(
	'[organizations] clone org position success',
	props<{ id: string }>(),
);
export const cloneOrgPositionFailure = createAction(
	'[organizations] clone org position failure',
);
export const cloneOrgPositionClear = createAction(
	'[organizations] clone org position clear',
);

export const editPositionRegStart = createAction(
	'[organizations] edit position registration start',
	props<{ id: string; positionId: string; request: PositionRequest }>(),
);
export const editPositionRegSuccess = createAction(
	'[organizations] edit position registration success',
);
export const editPositionRegFailure = createAction(
	'[organizations] edit position registration failure',
);

export const fetchOrgPoliciesStart = createAction(
	'[organizations] fetch org policies start',
	props<{ id: string; policyType: 'dental' | 'health' }>(),
);
export const fetchOrgPoliciesSuccess = createAction(
	'[organizations] fetch org policies success',
	props<{ policies: PolicySummary[] }>(),
);
export const fetchOrgPoliciesFailure = createAction(
	'[organizations] fetch org policies failure',
);
export const fetchOrgPoliciesClear = createAction(
	'[organizations] fetch org policies clear',
);

export const fetchPolicyRegStart = createAction(
	'[organizations] fetch policy registration start',
	props<{ id: string; policyId: string; policyType: string }>(),
);
export const fetchPolicyRegSuccess = createAction(
	'[organizations] fetch policy registration success',
	props<{ policy: PolicyRegistrationResponse }>(),
);
export const fetchPolicyRegFailure = createAction(
	'[organizations] fetch policy registration failure',
);

export const createPolicyStart = createAction(
	'[organizations] create policy start',
	props<{ id: string; request: any }>(),
);
export const createPolicySuccess = createAction(
	'[organizations] create policy success',
);
export const createPolicyFailure = createAction(
	'[organizations] create policy failure',
);

export const editPolicyRegStart = createAction(
	'[organizations] edit policy registration start',
	props<{ id: string; policyId: string; request: any }>(),
);
export const editPolicyRegSuccess = createAction(
	'[organizations] edit policy registration success',
);
export const editPolicyRegFailure = createAction(
	'[organizations] edit policy registration failure',
);

export const fetchOrgsSummaryListStart = createAction(
	'[organizations] fetch orgs summary list start',
	props<{ id: string }>(),
);
export const fetchOrgsSummaryListSuccess = createAction(
	'[organizations] fetch orgs summary list success',
	props<{ orgs: OrgListSummary[] }>(),
);
export const fetchOrgsSummaryListFailure = createAction(
	'[organizations] fetch orgs summary list failure',
);

export const fetchOrgGroupsStart = createAction(
	'[organizations] fetch org groups start',
	props<{ id: string }>(),
);
export const fetchOrgGroupsSuccess = createAction(
	'[organizations] fetch org groups success',
	props<{ groups: GroupsListSummary[] }>(),
);
export const fetchOrgGroupsFailure = createAction(
	'[organizations] fetch org groups failure',
);

export const fetchCpfAvailabilityStart = createAction(
	'[organizations] fetch cpf availability start',
	props<{ cpf: string }>(),
);
export const fetchCpfAvailabilitySuccess = createAction(
	'[organizations] fetch cpf availability success',
	props<{ result: boolean }>(),
);
export const fetchCpfAvailabilityFailure = createAction(
	'[organizations] fetch cpf availability failure',
);
export const fetchCpfAvailabilityClear = createAction(
	'[organizations] fetch cpf availability clear',
);

export const fetchEmailAvailabilityStart = createAction(
	'[organizations] fetch email availability start',
	props<{ email: string }>(),
);
export const fetchEmailAvailabilitySuccess = createAction(
	'[organizations] fetch email availability success',
	props<{ result: boolean }>(),
);
export const fetchEmailAvailabilityFailure = createAction(
	'[organizations] fetch email availability failure',
);
export const fetchEmailAvailabilityClear = createAction(
	'[organizations] fetch email availability clear',
);

export const fetchOrgUsersStart = createAction(
	'[organizations] fetch org users start',
	props<{ id: string; status: 'active' | 'inactive' }>(),
);
export const fetchOrgUsersSuccess = createAction(
	'[organizations] fetch org users success',
	props<{ users: OrgUser[] }>(),
);
export const fetchOrgUsersFailure = createAction(
	'[organizations] fetch org users failure',
);
export const fetchOrgUsersClear = createAction(
	'[organizations] fetch org users clear',
);

export const fetchUserRegStart = createAction(
	'[organizations] fetch user registration start',
	props<{ id: string; userId: string; userType: string }>(),
);
export const fetchUserRegSuccess = createAction(
	'[organizations] fetch user registration success',
	props<{ user: UserRegistration }>(),
);
export const fetchUserRegFailure = createAction(
	'[organizations] fetch user registration failure',
);

export const createUserStart = createAction(
	'[organizations] create user start',
	props<{ id: string; fromBene: boolean; request: UserRequest }>(),
);
export const createUserSuccess = createAction(
	'[organizations] create user success',
);
export const createUserFailure = createAction(
	'[organizations] create user failure',
);

export const editUserRegStart = createAction(
	'[organizations] edit user registration start',
	props<{
		id: string;
		fromBene: boolean;
		request: UserEditRequest;
		userId: string;
	}>(),
);
export const editUserRegSuccess = createAction(
	'[organizations] edit user registration success',
);
export const editUserRegFailure = createAction(
	'[organizations] edit user registration failure',
);

export const fetchOrgBeneficiariesStart = createAction(
	'[organizations] fetch org beneficiaries start',
	props<{
		id: string;
		sort: string;
		status: BeneficiaryStatusFilter;
		term: string;
	}>(),
);
export const fetchOrgBeneficiariesSuccess = createAction(
	'[organizations] fetch org beneficiaries success',
	props<{ beneficiaries: OrgBeneficiary[] }>(),
);
export const fetchOrgBeneficiariesSuccessWithCount = createAction(
	'[organizations] fetch org beneficiaries success with count',
	props<{
		beneficiaries: OrgBeneficiary[];
		completeCount: number;
		pendencyCount: number;
	}>(),
);
export const fetchOrgBeneficiariesSuccessWithInactiveDeletedCount =
	createAction(
		'[organizations] fetch org beneficiaries success with inactive deleted count',
		props<{ beneficiaries: OrgBeneficiary[]; inactiveDeletedCount: number }>(),
	);
export const fetchOrgBeneficiariesFailure = createAction(
	'[organizations] fetch org beneficiaries failure',
);
export const fetchOrgBeneficiariesClear = createAction(
	'[organizations] fetch org beneficiaries clear',
);

export const fetchCpfStatusStart = createAction(
	'[organizations] fetch org cpf status for beneficiary start',
	props<{ cpf: string; orgId: string }>(),
);
export const fetchCpfStatusSuccess = createAction(
	'[organizations] fetch org cpf status for beneficiary success',
	props<{ data: UserDataModel; result: number }>(),
);
export const fetchCpfStatusFailure = createAction(
	'[organizations] fetch org cpf status for beneficiary failure',
);
export const fetchCpfStatusClear = createAction(
	'[organizations] fetch org cpf status for beneficiary clear',
);

export const fetchCpfTitularStart = createAction(
	'[organizations] fetch org cpf titular availability start',
	props<{ cpf: string; orgId: string }>(),
);
export const fetchCpfTitularSuccess = createAction(
	'[organizations] fetch org cpf titular availability success',
	props<{ result: boolean }>(),
);
export const fetchCpfTitularFailure = createAction(
	'[organizations] fetch org cpf titular availability failure',
);
export const fetchCpfTitularClear = createAction(
	'[organizations] fetch org cpf titular availability clear',
);

export const fetchCpfDependentStart = createAction(
	'[organizations] fetch org cpf dependent availability start',
	props<{ cpf: string; orgId: string }>(),
);
export const fetchCpfDependentSuccess = createAction(
	'[organizations] fetch org cpf dependent availability success',
	props<{ result: boolean }>(),
);
export const fetchCpfDependentFailure = createAction(
	'[organizations] fetch org cpf dependent availability failure',
);
export const fetchCpfDependentClear = createAction(
	'[organizations] fetch org cpf dependent availability clear',
);

export const fetchEmailDependentStart = createAction(
	'[organizations] fetch org email dependent availability start',
	props<{ email: string; orgId: string }>(),
);
export const fetchEmailDependentSuccess = createAction(
	'[organizations] fetch org email dependent availability success',
	props<{ result: boolean }>(),
);
export const fetchEmailDependentFailure = createAction(
	'[organizations] fetch org email dependent availability failure',
);
export const fetchEmailDependentClear = createAction(
	'[organizations] fetch org email dependent availability clear',
);

export const createDependentStart = createAction(
	'[organizations] create dependent start',
	props<{ cpf: string; orgId: string; request: any }>(),
);
export const createDependentSuccess = createAction(
	'[organizations] create dependent success',
	props<{ depModalResult: boolean }>(),
);
export const createDependentFailure = createAction(
	'[organizations] create dependent failure',
);
export const createDependentClear = createAction(
	'[organizations] create dependent clear',
);

export const fetchDependentRegStart = createAction(
	'[organizations] fetch dependent registration start',
	props<{ depId: string; orgId: string }>(),
);
export const fetchDependentRegSuccess = createAction(
	'[organizations] fetch dependent registration success',
	props<{ dep: any }>(),
);
export const fetchDependentRegFailure = createAction(
	'[organizations] fetch dependent registration failure',
);
export const fetchDependentRegClear = createAction(
	'[organizations] fetch dependent registration clear',
);

export const editDependentRegStart = createAction(
	'[organizations] edit dependent registration start',
	props<{
		dependentId: string;
		request: BeneficiaryDependentUpdateDto;
	}>(),
);
export const editDependentRegSuccess = createAction(
	'[organizations] edit dependent registration success',
);
export const editDependentRegFailure = createAction(
	'[organizations] edit dependent registration failure',
);

export const deleteDependentStart = createAction(
	'[organizations] delete dependent start',
	props<{ cpf: string; depId: string; orgId: string }>(),
);
export const deleteDependentSuccess = createAction(
	'[organizations] delete dependent success',
);
export const deleteDependentFailure = createAction(
	'[organizations] delete dependent failure',
);
export const deleteDependentClear = createAction(
	'[organizations] delete dependent clear',
);

export const fetchCreatedBeneficiaryIdStart = createAction(
	'[organizations] fetch created beneficiary id start',
	props<{ id: string }>(),
);
export const fetchCreatedBeneficiaryIdClear = createAction(
	'[organizations] fetch created beneficiary id clear',
);

export const keepCpfTitularStart = createAction(
	'[organizations] keep cpf titular start',
	props<{ cpf: string }>(),
);
export const keepCpfTitularClear = createAction(
	'[organizations] keep cpf titular clear',
);

export const fetchBeneficiaryRegStart = createAction(
	'[organizations] fetch beneficiary registration start',
	props<{ id: string; beneficiaryId: string }>(),
);
export const fetchBeneficiaryRegSuccess = createAction(
	'[organizations] fetch beneficiary registration success',
	props<{ beneficiary: any }>(),
);
export const fetchBeneficiaryRegFailure = createAction(
	'[organizations] fetch beneficiary registration failure',
);

export const editBeneficiaryRegStart = createAction(
	'[organizations] edit beneficiary registration start',
	props<{ id: string; beneficiaryId: string; create: boolean; request: any }>(),
);
export const editBeneficiaryRegSuccess = createAction(
	'[organizations] edit beneficiary registration success',
	props<{ result: number }>(),
);
export const editBeneficiaryRegFailure = createAction(
	'[organizations] edit beneficiary registration failure',
);

export const editRetiredOrInactiveRegStart = createAction(
	'[organizations] edit retired or inactive beneficiary start',
	props<{
		beneficiaryId: string;
		cpf: string;
		is_deleted: boolean;
		orgId: string;
		status: string;
	}>(),
);
export const editRetiredOrInactiveRegSuccess = createAction(
	'[organizations] edit retired or inactive beneficiary success',
);
export const editRetiredOrInactiveRegFailure = createAction(
	'[organizations] edit retired or inactive beneficiary failure',
);

export const editBeneficiaryTypeRegStart = createAction(
	'[organizations] edit beneficiary type registration start',
	props<{ id: string; beneId: string; beneType: string; cpf: string }>(),
);
export const editBeneficiaryTypeRegSuccess = createAction(
	'[organizations] edit beneficiary type registration success',
);
export const editBeneficiaryTypeRegFailure = createAction(
	'[organizations] edit beneficiary type registration failure',
);
export const editBeneficiaryClearCompleAct = createAction(
	'[organizations] clear edit beneficiary complementary action',
);

export const fetchBeneficiaryViewStart = createAction(
	'[organizations] fetch beneficiary view start',
	props<{ id: string; beneficiaryId: string; beneficiaryType: string }>(),
);
export const fetchBeneficiaryViewSuccess = createAction(
	'[organizations] fetch beneficiary view success',
	props<{ beneficiaryView: TitularRegistration }>(),
);
export const fetchBeneficiaryViewFailure = createAction(
	'[organizations] fetch beneficiary view failure',
);

export const fetchBeneficiaryWalletsStart = createAction(
	'[organizations] fetch beneficiary wallets start',
	props<{ id: string; beneficiaryId: string }>(),
);
export const fetchBeneficiaryWalletsSuccess = createAction(
	'[organizations] fetch beneficiary wallets success',
	props<{ beneficiaryWallets: BeneficiaryBenefitData[] }>(),
);
export const fetchBeneficiaryWalletsFailure = createAction(
	'[organizations] fetch beneficiary wallets failure',
);

export const fetchBeneficiaryAllBenefitsStart = createAction(
	'[organizations] fetch beneficiary all benefits start',
	props<{ id: string; positionId: string }>(),
);
export const fetchBeneficiaryAllBenefitsSuccess = createAction(
	'[organizations] fetch beneficiary all benefits success',
	props<{ beneficiaryAllBenefits: BeneficiaryInfoResponse }>(),
);
export const fetchBeneficiaryAllBenefitsFailure = createAction(
	'[organizations] fetch beneficiary all benefits failure',
);

export const fetchBeneficiaryAllPoliciesStart = createAction(
	'[organizations] fetch beneficiary all policies start',
	props<{ id: string; positionId: string }>(),
);
export const fetchBeneficiaryAllPoliciesSuccess = createAction(
	'[organizations] fetch beneficiary all policies success',
	props<{ beneficiaryAllPolicies: BeneficiaryInfoResponse }>(),
);
export const fetchBeneficiaryAllPoliciesFailure = createAction(
	'[organizations] fetch beneficiary all policies failure',
);

export const fetchBeneficiaryPoliciesStart = createAction(
	'[organizations] fetch beneficiary policies start',
	props<{ id: string; beneficiaryId: string }>(),
);
export const fetchBeneficiaryPoliciesSuccess = createAction(
	'[organizations] fetch beneficiary policies success',
	props<{ beneficiaryPolicies: BeneficiaryInfoResponse }>(),
);
export const fetchBeneficiaryPoliciesFailure = createAction(
	'[organizations] fetch beneficiary policies failure',
);

export const fetchBeneficiaryViewClear = createAction(
	'[organizations] fetch beneficiary view clear',
);

export const editBeneficiaryStatusStart = createAction(
	'[organizations] edit beneficiary status start',
	props<{ request: EditBeneStatusRequest }>(),
);
export const editBeneficiaryStatusSuccess = createAction(
	'[organizations] edit beneficiary status success',
	props<{ status: string }>(),
);
export const editBeneficiaryStatusFailure = createAction(
	'[organizations] edit beneficiary status failure',
);
export const editBeneficiaryStatusClear = createAction(
	'[organizations] edit beneficiary status clear',
);

export const editBeneficiaryBenefitsStart = createAction(
	'[organizations] edit beneficiary benefits start',
	props<{ benId: string; request: any }>(),
);
export const editBeneficiaryBenefitsSuccess = createAction(
	'[organizations] edit beneficiary benefits success',
);
export const editBeneficiaryBenefitsFailure = createAction(
	'[organizations] edit beneficiary benefits failure',
);
export const editBeneficiaryBenefitsClear = createAction(
	'[organizations] edit beneficiary benefits clear',
);

export const editBeneficiaryPoliciesStart = createAction(
	'[organizations] edit beneficiary policies start',
	props<{ benId: string; request: any }>(),
);
export const editBeneficiaryPoliciesSuccess = createAction(
	'[organizations] edit beneficiary policies success',
);
export const editBeneficiaryPoliciesFailure = createAction(
	'[organizations] edit beneficiary policies failure',
);
export const editBeneficiaryPoliciesClear = createAction(
	'[organizations] edit beneficiary policies clear',
);

export const fetchDependentBenefitsStart = createAction(
	'[organizations] fetch dependent benefits start',
	props<{ id: string }>(),
);
export const fetchDependentBenefitsSuccess = createAction(
	'[organizations] fetch dependent benefits success',
	props<{ benefits: any }>(),
);
export const fetchDependentBenefitsFailure = createAction(
	'[organizations] fetch dependent benefits failure',
);
export const fetchDependentBenefitsClear = createAction(
	'[organizations] fetch dependent benefits clear',
);

export const fetchDependentWalletsStart = createAction(
	'[organizations] fetch dependent wallets start',
	props<{ id: string }>(),
);
export const fetchDependentWalletsSuccess = createAction(
	'[organizations] fetch dependent wallets success',
	props<{ wallets: any }>(),
);
export const fetchDependentWalletsFailure = createAction(
	'[organizations] fetch dependent wallets failure',
);
export const fetchDependentWalletsClear = createAction(
	'[organizations] fetch dependent wallets clear',
);

export const editDependentBenefitsStart = createAction(
	'[organizations] edit dependent benefits start',
	props<{ depId: string; request: any }>(),
);
export const editDependentBenefitsSuccess = createAction(
	'[organizations] edit dependent benefits success',
);
export const editDependentBenefitsFailure = createAction(
	'[organizations] edit dependent benefits failure',
);
export const editDependentBenefitsClear = createAction(
	'[organizations] edit dependent benefits clear',
);

export const saveBeneficiaryFilesStart = createAction(
	'[organizations] save beneficiary files start',
	props<{
		id: string;
		beneficiaryId: string;
		inactive: boolean;
		request: any;
	}>(),
);
export const saveBeneficiaryFilesSuccess = createAction(
	'[organizations] save beneficiary files success',
);
export const saveBeneficiaryFilesFailure = createAction(
	'[organizations] save beneficiary files failure',
);
export const deleteBeneficiaryFileStart = createAction(
	'[organizations] delete beneficiary file start',
	props<{ fileId: string }>(),
);
export const deleteBeneficiaryFileSuccess = createAction(
	'[organizations] delete beneficiary file success',
);
export const deleteBeneficiaryFileFailure = createAction(
	'[organizations] delete beneficiary file failure',
);
export const clearBeneficiaryFileResult = createAction(
	'[organizations] beneficiary file result clear',
);

export const fetchBeneficiaryFileURLStart = createAction(
	'[organizations] fetch beneficiary file URL start',
	props<{ fileId: string }>(),
);
export const fetchBeneficiaryFileURLSuccess = createAction(
	'[organizations] fetch beneficiary file URL success',
	props<{ url: string }>(),
);
export const fetchBeneficiaryFileURLFailure = createAction(
	'[organizations] fetch beneficiary file URL failure',
);
export const fetchBeneficiaryFileURLClear = createAction(
	'[organizations] fetch beneficiary file URL clear',
);

export const fetchBeneficiaryNotificationsStart = createAction(
	'[organizations] fetch beneficiary notifications start',
	props<{ benId: string }>(),
);
export const fetchBeneficiaryNotificationsSuccess = createAction(
	'[organizations] fetch beneficiary notifications success',
	props<{ notifications: INotification[] }>(),
);
export const fetchBeneficiaryNotificationsFailure = createAction(
	'[organizations] fetch beneficiary notifications failure',
);

export const deleteBeneficiaryNotificationStart = createAction(
	'[organizations] delete beneficiary notification start',
	props<{ id: string }>(),
);
export const deleteBeneficiaryNotificationSuccess = createAction(
	'[organizations] delete beneficiary notification success',
	props<{ id: string }>(),
);

export const deleteOrgPositionStart = createAction(
	'[organizations] delete org position start',
	props<{ id: string; orgId: string }>(),
);
export const deleteOrgPositionSuccess = createAction(
	'[organizations] delete org position success',
);
export const deleteOrgPositionFailure = createAction(
	'[organizations] delete org position failure',
);

export const deleteOrgBenefitStart = createAction(
	'[organizations] delete org benefit start',
	props<{ id: string; orgId: string }>(),
);
export const deleteOrgBenefitSuccess = createAction(
	'[organizations] delete org benefit success',
);
export const deleteOrgBenefitFailure = createAction(
	'[organizations] delete org benefit failure',
);

export const deleteOrgPolicyStart = createAction(
	'[organizations] delete org policy start',
	props<{ id: string; orgId: string }>(),
);
export const deleteOrgPolicySuccess = createAction(
	'[organizations] delete org policy success',
);
export const deleteOrgPolicyFailure = createAction(
	'[organizations] delete org policy failure',
);

export const fetchOrganizationById = createAction(
	'[organizations] fetch organization by id',
	props<{ id: string }>(),
);

export const fetchOrganizationByIdSuccess = createAction(
	'[organizations] fetch organization by id success',
	props<{ org: OrganizationDto }>(),
);

export const fetchOrganizationByIdFailure = createAction(
	'[organizations] fetch organization by id failure',
);

export const fetchBulkActionFileUrlStart = createAction(
	'[organizations] get org bulk action file url start',
	props<{ actionType: TemplateFileType; orgId: string }>(),
);
export const fetchBulkActionFileUrlSuccess = createAction(
	'[organizations] get org bulk action file url success',
	props<{ url: string }>(),
);
export const fetchBulkActionFileUrlFailure = createAction(
	'[organizations] get org bulk action file url failure',
);
export const clearBulkFileUrl = createAction(
	'[organizations] clear org bulk file url',
);

export const uploadBulkActionFileStart = createAction(
	'[organizations] upload org bulk action file start',
	props<{ actionType: TemplateFileType; request: any }>(),
);
export const uploadBulkActionFileSuccess = createAction(
	'[organizations] upload org bulk action file success',
);
export const uploadBulkActionFileFailure = createAction(
	'[organizations] upload org bulk action file failure',
);
export const clearBulkUploadResult = createAction(
	'[organizations] clear org bulk upload result',
);

export const fetchScoresStart = createAction(
	'[organizations] fetch scores start',
	props<{ search: string | null }>(),
);
export const fetchScoresSuccess = createAction(
	'[organizations] fetch scores success',
	props<{ scores: Score[] }>(),
);
export const fetchScoresFailure = createAction(
	'[organizations] fetch scores failure',
);

export const fetchScoresXLSXUrlStart = createAction(
	'[organizations] fetch scores xlsx url start',
);
export const fetchScoresXLSXUrlSuccess = createAction(
	'[organizations] fetch scores xlsx url success',
	props<{ url: string }>(),
);
export const fetchScoresXLSXUrlFailure = createAction(
	'[organizations] fetch scores xlsx url failure',
);
export const clearScoresXLSXUrl = createAction(
	'[organizations] clear scores xlsx url',
);

export const deleteStepClear = createAction(
	'[organizations] delete step clear',
);

export const fetchDashboardReportsStart = createAction(
	'[organizations] fetch dashboard reports start',
	props<{ orgId: string }>(),
);
export const fetchDashboardReportsSuccess = createAction(
	'[organizations] fetch dashboard reports success',
	props<{ report: DashReportChart[] }>(),
);
export const fetchDashboardReportsFailure = createAction(
	'[organizations] fetch dashboard reports failure',
);

export const fetchDashboardBeneInfoStart = createAction(
	'[organizations] fetch dashboard beneficiary info start',
	props<{ orgId: string }>(),
);
export const fetchDashboardBeneInfoSuccess = createAction(
	'[organizations] fetch dashboard beneficiary info success',
	props<{ panel: DashBeneInfo }>(),
);
export const fetchDashboardBeneInfoFailure = createAction(
	'[organizations] fetch dashboard beneficiary info failure',
);

export const fetchDashOrgURLsStart = createAction(
	'[organizations] fetch dash org urls start',
);
export const fetchDashOrgURLsSuccess = createAction(
	'[organizations] fetch dash org urls success',
	props<{ reports: DashChartInfo[] }>(),
);
export const fetchDashOrgURLsFailure = createAction(
	'[organizations] fetch dash org urls failure',
);
export const setEditingDashOrgURL = createAction(
	'[organizations] set editing org url start',
	props<{ link: DashChartInfo }>(),
);

export const saveDashOrgURLStart = createAction(
	'[organizations] save dash org url start',
	props<{ linkId?: string; orgId: string; request: DashChartInfo }>(),
);
export const saveDashOrgURLSuccess = createAction(
	'[organizations] save dash org url success',
);
export const saveDashOrgURLFailure = createAction(
	'[organizations] save dash org url failure',
);

export const fetchOrgBeneficiariesDataURLStart = createAction(
	'[organizations] fetch org beneficiaries data url start',
	props<{ isMatriz?: boolean; orgId: string }>(),
);

export const fetchOrgBeneficiariesDataURLSuccess = createAction(
	'[organizations] fetch org beneficiaries data url success',
	props<{ url: string }>(),
);
export const fetchOrgBeneficiariesDataURLFailure = createAction(
	'[organizations] fetch org beneficiaries data url failure',
);
export const clearOrgBeneficiariesDataURL = createAction(
	'[organizations] clear org beneficiaries data url',
);

export const fetchOrgBeneficiaryPendenciesStart = createAction(
	'[organizations] fetch org beneficiary pendencies start',
	props<{ beneficiaryId: string }>(),
);
export const fetchOrgBeneficiaryPendenciesSuccess = createAction(
	'[organizations] fetch org beneficiary pendencies success',
	props<{ pendencies: OrgBeneficiaryPendencies }>(),
);
export const fetchOrgBeneficiaryPendenciesFailure = createAction(
	'[organizations] fetch org beneficiary pendencies failure',
);

export const patchOrgBeneficiaryPendenciesStart = createAction(
	'[organizations] patch org beneficiary pendencies start',
	props<{
		beneficiaryId: string;
		pendencies: OrgBeneficiaryPendenciesRequest;
	}>(),
);
export const patchOrgBeneficiaryPendenciesSuccess = createAction(
	'[organizations] patch org beneficiary pendencies success',
);
export const patchOrgBeneficiaryPendenciesFailure = createAction(
	'[organizations] patch org beneficiary pendencies failure',
);

export const deleteOrgBeneficiaryPendencyStart = createAction(
	'[organizations] delete org beneficiary pendencies start',
	props<{ pendencyId: string }>(),
);
export const deleteOrgBeneficiaryPendencySuccess = createAction(
	'[organizations] delete org beneficiary pendencies success',
);
export const deleteOrgBeneficiaryPendencyFailure = createAction(
	'[organizations] delete org beneficiary pendencies failure',
);
export const clearOrgBeneficiaryPendenciesResults = createAction(
	'[organizations] clear org beneficiary pendencies results',
);

export const fetchOrgBatchActionsStart = createAction(
	'[organizations] fetch org batch actions start',
	props<{
		operationType?: OperationType;
		orgId: string;
		status?: UploadStatusFilter;
	}>(),
);

export const setBatchActionsFilters = createAction(
	'[organizations] set batch actions filters',
	props<{
		operationType: 'all' | OperationType;
		status: UploadStatusFilter;
	}>(),
);

export const clearBatchActionsFilters = createAction(
	'[organizations] clear batch actions filters',
);
export const fetchOrgBatchActionsSuccess = createAction(
	'[organizations] fetch org batch actions success',
	props<{ batchActions: any }>(),
);
export const fetchOrgBatchActionsFailure = createAction(
	'[organizations] fetch org batch actions failure',
);

export const fetchOrgBatchActionInfoStart = createAction(
	'[organizations] fetch org batch action info start',
	props<{ id: string }>(),
);
export const fetchOrgBatchActionInfoSuccess = createAction(
	'[organizations] fetch org batch action info success',
	props<{ batchAction: any }>(),
);
export const fetchOrgBatchActionInfoFailure = createAction(
	'[organizations] fetch org batch action info failure',
);

export const fetchOrgBatchActionFileStart = createAction(
	'[organizations] fetch org batch action file start',
	props<{ id: string }>(),
);
export const fetchOrgBatchActionFileSuccess = createAction(
	'[organizations] fetch org batch action file success',
	props<{ url: string }>(),
);
export const fetchOrgBatchActionFileFailure = createAction(
	'[organizations] fetch org batch action file failure',
);
export const fetchOrgBatchActionFileClear = createAction(
	'[organizations] fetch org batch action file clear',
);

export const downloadOrgBatchActionFileStart = createAction(
	'[organizations] download org batch action file start',
	props<{ url: string }>(),
);
export const downloadOrgBatchActionFileSuccess = createAction(
	'[organizations] download org batch action file success',
	props<{ blob: Blob }>(),
);
export const downloadOrgBatchActionFileFailure = createAction(
	'[organizations] download org batch action file failure',
);
export const downloadOrgBatchActionFileClear = createAction(
	'[organizations] download org batch action file clear',
);

export const fetchOrgProducts = createAction(
	'[organizations] fetch org products',
);
export const fetchOrgProductsSuccess = createAction(
	'[organizations] fetch org products success',
	props<{ products: ProductsSummary[] }>(),
);
